import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Monogram } from "./Monogram";
import { HERO_SLIDES } from "../../data/content";

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 34 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 6500);
        return () => clearInterval(t);
    }, [paused]);

    const go = (dir) => setIndex((i) => (i + dir + HERO_SLIDES.length) % HERO_SLIDES.length);

    return (
    <section
        id="top"
        data-testid="hero-section"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="group relative flex min-h-screen items-center justify-center overflow-hidden bg-emerald-deep"
    >
        <div className="absolute inset-0">
            <AnimatePresence>
                <motion.img
                    key={HERO_SLIDES[index].src}
                    src={HERO_SLIDES[index].src}
                    alt={HERO_SLIDES[index].alt}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1.15 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        opacity: { duration: 1.8, ease: "easeInOut" },
                        scale: { duration: 9, ease: "linear" },
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-deep/80 via-emerald-deep/45 to-emerald-deep/85" />
        </div>

        <button
            data-testid="hero-prev-btn"
            onClick={() => go(-1)}
            aria-label="Previous hero image"
            className="absolute left-5 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-gold opacity-0 transition-[opacity,background-color,color] duration-500 ease-elegant hover:bg-gold hover:text-emerald-deep group-hover:opacity-100 lg:left-10"
        >
            <ChevronLeft size={20} />
        </button>
        <button
            data-testid="hero-next-btn"
            onClick={() => go(1)}
            aria-label="Next hero image"
            className="absolute right-5 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-gold opacity-0 transition-[opacity,background-color,color] duration-500 ease-elegant hover:bg-gold hover:text-emerald-deep group-hover:opacity-100 lg:right-10"
        >
            <ChevronRight size={20} />
        </button>

        <div className="relative z-10 flex flex-col items-center px-6 pt-24 pb-16 text-center">
            <motion.div {...fadeUp(0.15)}>
                <Monogram size={130} className="glow-pulse drop-shadow-[0_0_25px_rgba(201,162,39,0.35)]" />
            </motion.div>

            <motion.p {...fadeUp(0.4)} className="mt-10 text-[11px] font-medium uppercase tracking-[0.6em] text-gold-pale">
                Events By
            </motion.p>

            <motion.h1
                {...fadeUp(0.55)}
                data-testid="hero-title"
                className="mt-3 font-serif text-5xl uppercase tracking-[0.18em] text-ivory sm:text-6xl lg:text-7xl"
            >
                Leemerh
            </motion.h1>

            <motion.p {...fadeUp(0.75)} className="mt-6 font-script text-4xl text-gold sm:text-5xl" data-testid="hero-tagline">
                Where Elegant Moments Begin
            </motion.p>

            <motion.div {...fadeUp(0.9)} className="mt-4 flex items-center gap-4" aria-hidden="true">
                <span className="h-px w-16 bg-gold/60" />
                <svg width="14" height="14" viewBox="0 0 24 24">
                    <path d="M12 20.5 c-7-4.5-10-8.2-10-12 A5.5 5.5 0 0 1 12 5 a5.5 5.5 0 0 1 10 3.5 c0 3.8-3 7.5-10 12z" fill="#C9A227" opacity="0.9" />
                </svg>
                <span className="h-px w-16 bg-gold/60" />
            </motion.div>

            <motion.p {...fadeUp(1.0)} className="mt-6 max-w-xl text-sm leading-relaxed text-ivory/75 sm:text-base">
                Turning moments into memories — thoughtfully curated celebrations,
                tailored to your unique vision and style.
            </motion.p>

            <motion.div {...fadeUp(1.15)} className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
                <a
                    href="#contact"
                    data-testid="hero-book-consultation-btn"
                    className="btn-sweep bg-gold px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-deep shadow-[0_10px_40px_-8px_rgba(201,162,39,0.5)] transition-[transform,box-shadow] duration-500 ease-elegant hover:scale-[1.04] hover:shadow-[0_16px_50px_-8px_rgba(201,162,39,0.65)]"
                >
                    Book a Consultation
                </a>
                <a
                    href="#gallery"
                    data-testid="hero-explore-gallery-btn"
                    className="border border-ivory/40 px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory/90 transition-[border-color,color,transform] duration-500 ease-elegant hover:scale-[1.04] hover:border-gold hover:text-gold"
                >
                    Explore Our Work
                </a>
            </motion.div>
        </div>

        <div
            data-testid="hero-carousel-dots"
            className="absolute bottom-24 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3"
        >
            {HERO_SLIDES.map((s, i) => (
                <button
                    key={s.src}
                    data-testid={`hero-slide-dot-${i}`}
                    onClick={() => setIndex(i)}
                    aria-label={`Show hero image ${i + 1}`}
                    className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ease-elegant ${
                        index === i ? "w-8 bg-gold" : "w-1.5 bg-ivory/40 hover:bg-gold/70"
                    }`}
                />
            ))}
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
            className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            aria-hidden="true"
        >
            <div className="flex h-12 w-7 items-start justify-center rounded-full border border-gold/50 p-2">
                <div className="scroll-drift h-2 w-1 rounded-full bg-gold" />
            </div>
        </motion.div>
    </section>
    );
};
